import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import env from '@/env'

// Shape of the stored user info
interface StoredUser {
	_id: string
	email: string
	role: string
	accessProjects: string[]
	accessToken: string
}

interface UserDataPayload {
	_id: string
	email: string
	role: string
	accessProjects: string[]
}

/**
 * Format a `ZodError`-shaped payload into a short, readable list — one line
 * per issue, prefixed with the field path. Used when `@hono/zod-validator`
 * rejects a request: it returns `{ success: false, error: { issues, name } }`
 * rather than the canonical `{ msg }`, so without this we'd dump the raw
 * JSON object into a toast.
 */
const formatZodIssues = (issues: any[]): string => {
	if (!Array.isArray(issues) || issues.length === 0) return 'validation failed'
	return issues
		.map((i) => {
			const path = Array.isArray(i?.path) && i.path.length ? i.path.join('.') : ''
			const message = typeof i?.message === 'string' ? i.message : 'invalid'
			return path ? `${path}: ${message}` : message
		})
		.join('; ')
}

const makeError = (msg: string, err: any): string => {
	console.error('🍒', msg, err)
	const data = err?.response?.data
	// Prefer the canonical Hono shape `{ msg: '…' }`.
	if (typeof data?.msg === 'string') return data.msg
	if (typeof data?.message === 'string') return data.message
	// `@hono/zod-validator` default shape on validation failure — surfaces
	// as a readable bullet list instead of the raw schema dump.
	if (data?.success === false && Array.isArray(data?.error?.issues)) {
		return formatZodIssues(data.error.issues)
	}
	if (Array.isArray(data?.issues)) return formatZodIssues(data.issues)
	if (typeof data === 'string') return data
	if (data && typeof data === 'object') return JSON.stringify(data)
	return err.message || String(err)
}

// Retrieves token and user-payload header data from localStorage
async function getUser(): Promise<{ token: string; data: string }> {
	const raw = localStorage.getItem('auth/user')
	const parsed = raw ? JSON.parse(raw) : {}
	const user: StoredUser = parsed.user || {}

	const token = user.accessToken || ''
	const payload: UserDataPayload = {
		_id: user._id || '',
		email: user.email || '',
		role: user.role || '',
		accessProjects: user.accessProjects || [],
	}

	return { token, data: JSON.stringify(payload) }
}

const instance: AxiosInstance = axios.create({
	baseURL: env.apiUrl,
	responseType: 'json',
	timeout: 300_000, // 300 seconds
})

// Attach auth headers to a config object
async function authConfig(method: string, url: string): Promise<AxiosRequestConfig> {
	const { token, data } = await getUser()
	return {
		headers: {
			Authorization: token || '',
			userdata: data || '',
		},
	}
}

const http = {
	// GET with auth
	// `signal` lets a caller cancel a request that a newer one replaced.
	async getAuth<T = any>(url: string, _headers = {}, opts: { signal?: AbortSignal } = {}): Promise<T> {
		try {
			console.log('GET AUTH', url)
			const config: AxiosRequestConfig = await authConfig('GET', url)
			if (!config.headers) config.headers = {}
			for (const key in _headers) config.headers[key] = _headers[key]
			if (opts.signal) config.signal = opts.signal
			const resp = await instance.get<T>(url, config)
			return resp.data
		} catch (err: any) {
			const msg = makeError(`http.getAuth ${url}`, err)
			throw new Error(msg)
		}
	},

	// Simple GET
	async get<T = any>(url: string, _headers: {} = {}): Promise<T> {
		try {
			console.log('GET', url)
			const config: AxiosRequestConfig = await authConfig('GET', url)
			if (!config.headers) config.headers = {}
			for (const key in _headers) config.headers[key] = _headers[key]
			const resp = await instance.get<T>(url, config)
			return resp.data
		} catch (err: any) {
			const msg = makeError(`http.get ${url}`, err)
			throw new Error(msg)
		}
	},

	// POST with auth and generic response
	async postAuth<T = any, B = any>(url: string, body: B, _headers: {} = {}): Promise<T> {
		try {
			console.log('POST AUTH', url)
			const config = await authConfig('POST', url)
			if (!config.headers) config.headers = {}
			for (const key in _headers) config.headers[key] = _headers[key]
			const resp = await instance.post<T>(url, body, config)
			return resp.data
		} catch (err: any) {
			const msg = makeError(`http.postAuth ${url}`, err)
			throw new Error(msg)
		}
	},

	// Simple POST
	async post<T = any, B = any>(url: string, body: B, _headers: {} = {}): Promise<T> {
		try {
			console.log('POST', url)
			const config = await authConfig('POST', url)
			if (!config.headers) config.headers = {}
			for (const key in _headers) config.headers[key] = _headers[key]
			const resp = await instance.post<T>(url, body, config)
			return resp.data
		} catch (err: any) {
			const msg = makeError(`http.post ${url}`, err)
			throw new Error(msg)
		}
	},

	// DELETE with auth
	async deleteAuth<T = any>(url: string): Promise<T> {
		try {
			console.log('DELETE AUTH', url)
			const config = await authConfig('DELETE', url)
			const resp = await instance.delete<T>(url, config)
			return resp.data
		} catch (err: any) {
			const msg = makeError(`http.deleteAuth ${url}`, err)
			throw new Error(msg)
		}
	},

	// PUT with auth
	async putAuth<T = any, B = any>(url: string, body: B): Promise<T> {
		try {
			console.log('PUT AUTH', url)
			const config = await authConfig('PUT', url)
			const resp = await instance.put<T>(url, body, config)
			return resp.data
		} catch (err: any) {
			const msg = makeError(`http.putAuth ${url}`, err)
			throw new Error(msg)
		}
	},
}

export default http
